"use client";

/**
 * Déblocage du badge du mois — plein écran après l'écran des quêtes, et
 * UNIQUEMENT quand le +N de cette leçon fait franchir la cible mensuelle
 * (jamais pour un badge gagné un autre jour).
 *
 * Registre trophée, clair COMME sombre : fond profond (le même que le mode
 * légendaire), soleil radial derrière le médaillon, révélation noir et blanc
 * → couleur (le langage de la collection : un badge non gagné est en gris),
 * pastille du mois, phrase au passé, un seul bouton.
 *
 * Parité 1:1 avec reps-app/src/features/lesson/BadgeCelebration.tsx.
 */

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { motion, useReducedMotion } from "motion/react";
import { useTranslations, useLocale } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import RepsContinueButton from "@/components/Reps/RepsContinueButton";
import { LEGENDARY_COLORS } from "@/lib/reps/legendary";
import { monthName } from "@/lib/quests/questMeta";

/** Révélation (gris → couleur) après la pose du médaillon. */
const REVEAL_MS = 1100;
const MEDALLION = 150;
const STAGE = 300;
/** Garde anti-double-tap : le bouton occupe les pixels de l'écran précédent. */
const TAP_GUARD_MS = 600;

export default function RepsBadgeCelebration({
    badge,
    onContinue,
    leaving,
    onSound,
}) {
    const t = useTranslations();
    const locale = useLocale();
    const reduce = useReducedMotion();
    const [still] = useState(() => !!reduce);

    const month = monthName(badge.month, locale);
    const [revealed, setRevealed] = useState(still);

    useEffect(() => {
        if (still) {
            // Mouvement réduit : pas de mise en scène, mais le son porte quand
            // même le sens de l'écran.
            if (onSound) onSound("streak");
            return undefined;
        }
        const timer = setTimeout(() => {
            setRevealed(true);
            // Le médaillon passe en couleur : c'est LE moment.
            if (onSound) onSound("streak");
            try {
                if (navigator.vibrate) navigator.vibrate([25, 40, 25]);
            } catch (e) {
                /* ignore */
            }
        }, REVEAL_MS);
        return () => clearTimeout(timer);
        // Chronologie figée à l'affichage.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [mountedAt] = useState(() => Date.now());
    const handleContinue = () => {
        if (Date.now() - mountedAt < TAP_GUARD_MS) return;
        onContinue();
    };

    const title = t("reps_badge_unlock_title");
    const subtitle = t("reps_badge_unlock_sub", {
        count: badge.questTarget,
        month,
    });

    const [announce, setAnnounce] = useState("");
    const rootRef = useRef(null);
    useEffect(() => {
        rootRef.current?.focus({ preventScroll: true });
        const id = setTimeout(
            () =>
                setAnnounce(
                    [title, badge.name, subtitle].filter(Boolean).join(". "),
                ),
            150,
        );
        return () => clearTimeout(id);
        // Résumé figé à l'affichage.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            ref={rootRef}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            tabIndex={-1}
            className="fixed inset-0 z-[60] overflow-y-auto bg-[#0E1020] text-white outline-hidden"
        >
            {/* Annoncé d'une traite : la révélation est visuelle, elle ne dit
                rien à qui ne la voit pas. Rempli APRÈS le montage, sinon la
                région live n'annonce rien. */}
            <p className="sr-only" role="status" aria-live="polite">
                {announce}
            </p>
            <div className="mx-auto flex min-h-full w-full max-w-md flex-col items-center justify-center px-6 py-10 text-center">
                <motion.span
                    initial={still ? false : { opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ type: "spring", stiffness: 240, damping: 22 }}
                    className="rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-widest"
                    style={{
                        backgroundColor: "rgba(245,201,66,0.14)",
                        color: LEGENDARY_COLORS.shine,
                    }}
                >
                    {month} {badge.year}
                </motion.span>

                {/* Scène du médaillon : soleil radial derrière, halo à la
                    révélation. */}
                <div
                    className="relative grid place-items-center"
                    style={{ width: STAGE, height: STAGE }}
                >
                    <motion.span
                        initial={still ? false : { opacity: 0.35, scale: 0.85 }}
                        animate={
                            revealed
                                ? { opacity: 1, scale: 1.05 }
                                : { opacity: 0.35, scale: 0.85 }
                        }
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        className="pointer-events-none absolute inset-0 rounded-full"
                        style={{
                            background: `radial-gradient(circle, rgba(245,201,66,0.28) 0%, rgba(245,201,66,0.10) 42%, rgba(14,16,32,0) 70%)`,
                        }}
                    />

                    {/* Onde de choc à l'instant de la révélation. */}
                    {revealed && !still ? (
                        <motion.span
                            initial={{ scale: 0.7, opacity: 0.55 }}
                            animate={{ scale: 2.1, opacity: 0 }}
                            transition={{ duration: 0.52, ease: "easeOut" }}
                            className="pointer-events-none absolute rounded-full border-2"
                            style={{
                                width: MEDALLION + 30,
                                height: MEDALLION + 30,
                                borderColor: LEGENDARY_COLORS.shine,
                            }}
                        />
                    ) : null}

                    <motion.div
                        initial={still ? false : { scale: 0.6, opacity: 0 }}
                        animate={
                            revealed && !still
                                ? { scale: [1, 1.12, 1], opacity: 1 }
                                : { scale: 1, opacity: 1 }
                        }
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        className="relative grid place-items-center"
                        style={{ width: MEDALLION, height: MEDALLION }}
                    >
                        {badge.imageUrl ? (
                            <>
                                {/* Les deux variantes empilées : la couleur
                                    fond par-dessus le gris à la révélation. */}
                                <Image
                                    src={badge.imageUrl}
                                    alt=""
                                    width={MEDALLION}
                                    height={MEDALLION}
                                    className="rounded-full opacity-70 grayscale"
                                />
                                <motion.span
                                    initial={{ opacity: still ? 1 : 0 }}
                                    animate={{ opacity: revealed ? 1 : 0 }}
                                    transition={{
                                        duration: 0.26,
                                        ease: "easeOut",
                                    }}
                                    className="absolute inset-0 grid place-items-center"
                                >
                                    <Image
                                        src={badge.imageUrl}
                                        alt={badge.name || ""}
                                        width={MEDALLION}
                                        height={MEDALLION}
                                        className="rounded-full"
                                    />
                                </motion.span>
                            </>
                        ) : (
                            <span
                                className="grid place-items-center rounded-full"
                                style={{
                                    width: MEDALLION,
                                    height: MEDALLION,
                                    backgroundColor: "rgba(245,201,66,0.16)",
                                }}
                            >
                                <SolarIcon
                                    name="medal-ribbon-star-bold"
                                    className="text-6xl"
                                    style={{ color: LEGENDARY_COLORS.shine }}
                                />
                            </span>
                        )}
                    </motion.div>
                </div>

                {/* Titre et phrase au passé : révélés avec le badge. */}
                <motion.div
                    initial={still ? false : { opacity: 0, y: 12 }}
                    animate={
                        revealed ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }
                    }
                    transition={{ duration: 0.32, ease: "easeOut" }}
                >
                    <h2
                        className="text-3xl font-extrabold"
                        style={{ color: LEGENDARY_COLORS.shine }}
                    >
                        {title}
                    </h2>
                    {badge.name ? (
                        <p className="mb-0 mt-2 text-[17px] font-extrabold text-white">
                            {badge.name}
                        </p>
                    ) : null}
                    <p className="mb-0 mt-2 text-[17px] leading-6 text-gray-400">
                        {subtitle}
                    </p>
                </motion.div>

                <RepsContinueButton
                    onClick={handleContinue}
                    loading={leaving}
                    className="mt-8"
                >
                    {t("reps_continue")}
                </RepsContinueButton>
            </div>
        </div>
    );
}
